// @flow
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import pipe from 'ramda/es/pipe';
import toPairs from 'ramda/es/toPairs';
import filter from 'ramda/es/filter';
import map from 'ramda/es/map';
import values from 'ramda/es/values';

import { Tabs, Tab, TabPanels, TabPanel } from 'app/common/ui/PlainTabs';
import Modal, { ModalHeader, ModalContent } from 'app/common/components/Modal';
import { availabilityFetch as availabilityStudentIdentifier } from 'app/actions/users/students/identifiers/availability';
import { availabilityFetch as availabilityEmployeeIdentifier } from 'app/actions/users/employees/identifiers/availability';
import { availabilityFetch as availabilityParentIdentifier } from 'app/actions/users/parents/identifiers/availability';
import { checkRequest as checkStudentIdentifier } from 'app/actions/users/students/identifiers/check';
import { checkRequest as checkEmployeeIdentifier } from 'app/actions/users/employees/identifiers/check';
import { checkRequest as checkParentIdentifier } from 'app/actions/users/parents/identifiers/check';
import { addIdentifierSubmit as addStudentIdentifier } from 'app/actions/users/students/identifiers/addIdentifierSubmit';
import { addIdentifierSubmit as addEmployeeIdentifier } from 'app/actions/users/employees/identifiers/addIdentifierSubmit';
import { addIdentifierSubmit as addParentIdentifier } from 'app/actions/users/parents/identifiers/addIdentifierSubmit';
import { AddingIdentifier } from 'app/common/components-next/identifiers';
import { popupErr } from 'app/common/actions/popup';

import LinkIdentifier from './LinkIdentifier';
import { OrderContext, type ReceiverItem } from '../../../OrderData';
import styles from './IdentifiersModal.module.scss';;


type Props = {
  toggleModal: () => void,
  selectedProductId: number,
  receiver: ReceiverItem,
};

const IdentifiersModal = ({
  toggleModal,
  selectedProductId,
  receiver,
}: Props) => {
  const { t } = useTranslation('shop');
  const dispatch = useDispatch();

  const [activeTabIndex, setActiveTabIndex] = useState(2);
  const [types, setTypes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const { id: personId, type: personType } = receiver;

  const requestAvailability = useCallback(async (...params) => {
    if ((process.env.APP_NAME || process.env.REACT_APP_NAME) !== 'journal') {
      const errorText = 'Не реализована функция запроса возможных идентификаторов для этого кабинета';
      dispatch(popupErr(errorText));
      throw new Error(errorText);
    }
    switch (personType) {
      case 'Student': {
        const res = await dispatch(availabilityStudentIdentifier(...params));
        return res;
      }
      case 'Employee': {
        const res = await dispatch(availabilityEmployeeIdentifier(...params));
        return res;
      }
      default: {
        const res = await dispatch(availabilityParentIdentifier(...params));
        return res;
      }
    }
  }, [dispatch, personType]);

  useEffect(() => {
    const run = async () => {
      const {
        types: typesRes,
        formats: formatsRes,
      } = await requestAvailability(personId);

      if (typesRes && formatsRes) {
        setTypes(values(typesRes));
        setFormats(pipe(
          toPairs,
          filter(([_, value]) => value), // eslint-disable-line no-unused-vars
          map(([key]) => key),
        )(formatsRes));
        setLoadingAvailability(false);
      }
    };
    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCheckIdentifier = useCallback(async (...params) => {
    if ((process.env.APP_NAME || process.env.REACT_APP_NAME) !== 'journal') {
      const errorText = 'Не реализована функция проверки идентификатора для этого кабинета';
      dispatch(popupErr(errorText));
      throw new Error(errorText);
    }
    switch (personType) {
      case 'Student': {
        const res = await dispatch(checkStudentIdentifier(...params));
        return res;
      }
      case 'Employee': {
        const res = await dispatch(checkEmployeeIdentifier(...params));
        return res;
      }
      default: {
        const res = await dispatch(checkParentIdentifier(...params));
        return res;
      }
    }
  }, [dispatch, personType]);

  const { request, content: { orderItemsNotIssued } } = useContext(OrderContext);

  const requestBeforeIdentifierAdd = useCallback(async () => {
    const items = [{
      order_item_id: selectedProductId,
      status: 'issued',
    }];
    const res = await request.issue(items);
    if (res) {
      return true;
    }
    return false;
  }, [request, selectedProductId]);

  const requestIdentifierSubmit = useCallback(async (...params) => {
    if ((process.env.APP_NAME || process.env.REACT_APP_NAME) !== 'journal') {
      const errorText = 'Не реализована функция добавления идентификатора из магазина для этого кабинета';
      dispatch(popupErr(errorText));
      throw new Error(errorText);
    }
    switch (personType) {
      case 'Student': {
        const res = await dispatch(addStudentIdentifier(...params));
        return res;
      }
      case 'Employee': {
        const res = await dispatch(addEmployeeIdentifier(...params));
        return res;
      }
      default: {
        const res = await dispatch(addParentIdentifier(...params));
        return res;
      }
    }
  }, [dispatch, personType]);

  const loading = loadingAvailability;

  return (
    <Modal toggleModal={toggleModal}>
      <ModalHeader>{t('identifier_add_modal_header')}</ModalHeader>
      <ModalContent className={styles.tabs}>
        <Tabs value={activeTabIndex} onChange={setActiveTabIndex}>
          <Tab index={1}>{t('identifier_add_modal_tab_add_new')}</Tab>
          <Tab index={2}>{t('identifier_add_modal_tab_issue')}</Tab>
        </Tabs>
      </ModalContent>

      <div className={styles.content}>
        <ModalContent
          maxHeight={540}
          className={styles.contentInside}
          scrollDownOnResize
        >
          <TabPanels value={activeTabIndex} className={styles.panels}>

            <TabPanel index={1}>
              <AddingIdentifier
                loading={loading}
                personId={personId}
                types={types}
                availableFormats={formats}
                onCancel={toggleModal}
                onIdentifierCheck={requestCheckIdentifier}
                onIdentifierSubmit={requestIdentifierSubmit}
              />
            </TabPanel>

            <TabPanel index={2}>
              <LinkIdentifier
                loading={loading}
                personId={personId}
                availableFormats={formats}
                orderItems={orderItemsNotIssued}
                types={types}
                selectedProductId={selectedProductId}
                onCancel={toggleModal}
                onIdentifierCheck={requestCheckIdentifier}
                onIdentifierSubmit={requestIdentifierSubmit}
                onBeforeIdentifierAdd={requestBeforeIdentifierAdd}
              />
            </TabPanel>

          </TabPanels>
        </ModalContent>
      </div>
    </Modal>
  );
};

export default IdentifiersModal;
